import { useState } from 'react';
import {
  Card,
  CardHeader,
  Button,
  Badge,
  Input,
  Select,
  Modal,
  LoadingSkeleton,
  useToast,
} from '@nexora/design-system';
import {
  useProductionOrders,
  useCreateProductionOrder,
  usePlanProductionOrder,
  useReleaseProductionOrder,
  useConsumeMaterial,
  usePostProductionOutput,
  useCloseProductionOrder,
  useCancelProductionOrder,
  useProductionOrderProgress,
  hasMaterialShortage,
  useProductionOrderCosts,
  useProductionOrderPostings,
  type ProductionOrder,
} from './useProductionOrders';
import { useBoms } from './useBoms';
import { formatCurrency, formatDate } from '../../shared/utils/format';

/* ── Helpers ── */

const STATUS_VARIANT: Record<string, 'neutral' | 'info' | 'warning' | 'success' | 'error'> = {
  DRAFT: 'neutral',
  PLANNED: 'info',
  RELEASED: 'warning',
  IN_PROGRESS: 'info',
  COMPLETED: 'success',
  CLOSED: 'success',
  CANCELLED: 'error',
};

const STATUS_LABEL: Record<string, string> = {
  DRAFT: 'Rascunho',
  PLANNED: 'Planejada',
  RELEASED: 'Liberada',
  IN_PROGRESS: 'Em Produção',
  COMPLETED: 'Concluída',
  CLOSED: 'Fechada',
  CANCELLED: 'Cancelada',
};

/* ── State transitions per status ── */
const NEXT_ACTIONS: Record<string, { key: string; label: string; color?: string }[]> = {
  DRAFT: [{ key: 'plan', label: 'Planejar' }],
  PLANNED: [{ key: 'release', label: 'Liberar' }],
  RELEASED: [
    { key: 'consume', label: 'Consumir Material' },
    { key: 'post-output', label: 'Apontar Produção' },
    { key: 'cancel', label: 'Cancelar', color: '#ba1a1a' },
  ],
  IN_PROGRESS: [
    { key: 'consume', label: 'Consumir Material' },
    { key: 'post-output', label: 'Apontar Produção' },
    { key: 'cancel', label: 'Cancelar', color: '#ba1a1a' },
  ],
  COMPLETED: [{ key: 'close', label: 'Fechar' }],
};

/* ── Page ── */

export function ProductionOrderPage(): JSX.Element {
  const { addToast } = useToast();
  const { data: boms } = useBoms();
  const activeBoms = boms?.data?.filter((b) => b.status === 'ACTIVE') ?? [];

  const [statusFilter, setStatusFilter] = useState('ALL');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const limit = 20;

  const { data, isLoading, error, refetch } = useProductionOrders({
    status: statusFilter !== 'ALL' ? statusFilter : undefined,
    search: searchTerm || undefined,
    page: page + 1,
    limit,
  });

  const orders = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / limit);

  /* Os quatro indicadores do chao de fabrica. Vem do servidor (`counts`) e nao
     da pagina carregada: a lista traz 20 registros e o painel diria "3
     atrasadas" havendo 11. "Atrasada" nao e status — e ordem aberta cujo fim
     planejado ja passou, derivada no servidor junto com as contagens. */
  const counts = data?.counts;
  const kpis = counts
    ? [
        {
          key: 'planned',
          icon: 'calendar_today',
          label: 'Planejadas',
          value: counts.byStatus.PLANNED + counts.byStatus.RELEASED,
          hint: `${counts.byStatus.RELEASED} liberada(s)`,
          tone: 'neutral' as const,
        },
        {
          key: 'in-progress',
          icon: 'settings',
          label: 'Em produção',
          value: counts.byStatus.IN_PROGRESS,
          hint: 'No chão de fábrica agora',
          tone: 'neutral' as const,
        },
        {
          key: 'delayed',
          icon: 'warning',
          label: 'Atrasadas',
          value: counts.delayed,
          hint: 'Fim planejado já passou',
          tone: 'warning' as const,
        },
        {
          key: 'completed',
          icon: 'check_circle',
          label: 'Concluídas',
          value: counts.completedSince,
          hint: 'Nos últimos 7 dias',
          tone: 'success' as const,
        },
      ]
    : [];

  // ── Modals ──
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [actionModal, setActionModal] = useState<{ id: string; action: string } | null>(null);

  // Create form
  const [form, setForm] = useState({
    productId: '',
    productName: '',
    orderNumber: '',
    plannedQuantity: '',
    scheduledStart: '',
    scheduledEnd: '',
    useActiveBom: true,
    notes: '',
  });

  // Action forms
  const [planForm, setPlanForm] = useState({ bomId: '', scheduledStart: '', scheduledEnd: '' });
  const [consumeForm, setConsumeForm] = useState({ productId: '', quantity: '', unitCost: '', lotNumber: '', notes: '' });
  const [postForm, setPostForm] = useState({ producedQuantity: '', wasteQuantity: '0', unitCost: '', lotNumber: '', notes: '' });
  const [cancelJustification, setCancelJustification] = useState('');
  const [releaseBomId, setReleaseBomId] = useState('');

  const createMutation = useCreateProductionOrder();
  const planMutation = usePlanProductionOrder();
  const releaseMutation = useReleaseProductionOrder();
  const consumeMutation = useConsumeMaterial();
  const postMutation = usePostProductionOutput();
  const closeMutation = useCloseProductionOrder();
  const cancelMutation = useCancelProductionOrder();

  const { data: progress } = useProductionOrderProgress(detailId ?? undefined);
  const { data: costs } = useProductionOrderCosts(detailId ?? undefined);
  const { data: postings } = useProductionOrderPostings(detailId ?? undefined);

  const resetForm = (): void => {
    setForm({ productId: '', productName: '', orderNumber: '', plannedQuantity: '', scheduledStart: '', scheduledEnd: '', useActiveBom: true, notes: '' });
  };

  const handleCreate = async (): Promise<void> => {
    if (!form.productId || !form.productName || !form.orderNumber || !form.plannedQuantity) {
      addToast('Preencha todos os campos obrigatórios', 'error');
      return;
    }
    try {
      await createMutation.mutateAsync({
        productId: form.productId,
        productName: form.productName,
        orderNumber: form.orderNumber,
        plannedQuantity: Number(form.plannedQuantity),
        scheduledStart: form.scheduledStart ? new Date(form.scheduledStart).toISOString() : '',
        scheduledEnd: form.scheduledEnd ? new Date(form.scheduledEnd).toISOString() : '',
        useActiveBom: form.useActiveBom,
        notes: form.notes || undefined,
      });
      addToast('Ordem de produção criada', 'success');
      setCreateOpen(false);
      resetForm();
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro ao criar ordem', 'error');
    }
  };

  const handleAction = async (): Promise<void> => {
    if (!actionModal) return;
    const { id, action } = actionModal;

    try {
      switch (action) {
        case 'plan': {
          if (!planForm.bomId || !planForm.scheduledStart || !planForm.scheduledEnd) {
            addToast('Preencha BOM, início e fim do planejamento', 'error');
            return;
          }
          await planMutation.mutateAsync({ id, ...planForm });
          addToast('Ordem planejada', 'success');
          break;
        }
        case 'release': {
          if (!releaseBomId) { addToast('Selecione uma BOM', 'error'); return; }
          await releaseMutation.mutateAsync({ id, bomId: releaseBomId });
          addToast('Ordem liberada', 'success');
          break;
        }
        case 'consume': {
          if (!consumeForm.productId || !consumeForm.quantity || !consumeForm.unitCost) {
            addToast('Preencha produto, quantidade e custo unitário', 'error');
            return;
          }
          await consumeMutation.mutateAsync({
            id,
            productId: consumeForm.productId,
            quantity: Number(consumeForm.quantity),
            unitCost: Number(consumeForm.unitCost),
            lotNumber: consumeForm.lotNumber || undefined,
            notes: consumeForm.notes || undefined,
          });
          addToast('Material consumido', 'success');
          break;
        }
        case 'post-output': {
          if (!postForm.producedQuantity || !postForm.unitCost) {
            addToast('Preencha quantidade produzida e custo unitário', 'error');
            return;
          }
          await postMutation.mutateAsync({
            id,
            producedQuantity: Number(postForm.producedQuantity),
            wasteQuantity: Number(postForm.wasteQuantity),
            unitCost: Number(postForm.unitCost),
            lotNumber: postForm.lotNumber || undefined,
            notes: postForm.notes || undefined,
          });
          addToast('Produção apontada', 'success');
          break;
        }
        case 'close': {
          await closeMutation.mutateAsync(id);
          addToast('Ordem fechada', 'success');
          break;
        }
        case 'cancel': {
          if (!cancelJustification.trim()) {
            addToast('Informe a justificativa de cancelamento', 'error');
            return;
          }
          await cancelMutation.mutateAsync({ id, justification: cancelJustification });
          addToast('Ordem cancelada', 'success');
          break;
        }
      }
      setActionModal(null);
      setDetailOpen(false);
    } catch (err) {
      addToast(err instanceof Error ? err.message : 'Erro na operação', 'error');
    }
  };

  return (
    <div className="prod-page">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <h1 className="font-display-lg text-display-lg text-on-surface m-0">Ordens de Produção</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">Acompanhamento do chão de fábrica: fila, progresso e apontamento.</p>
        </div>
        <div className="flex flex-wrap gap-sm">
          <Button onClick={() => { resetForm(); setCreateOpen(true); }}>+ Nova OP</Button>
        </div>
      </div>

      {/* ── Indicadores do chão de fábrica ── */}
      {kpis.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-md mb-lg">
          {kpis.map((k) => (
            <div key={k.key} className="prod-kpi">
              <div className="prod-kpi__top">
                <span className="material-symbols-outlined" aria-hidden="true">
                  {k.icon}
                </span>
                <span className="font-label-md text-label-md text-on-surface-variant uppercase">
                  {k.label}
                </span>
              </div>
              <div
                className={`font-display-lg text-display-lg tabular-nums ${
                  k.tone === 'warning' && k.value > 0 ? 'text-warning-amber' : 'text-on-surface'
                }`}
              >
                {k.value}
              </div>
              <p className="font-body-sm text-body-sm text-on-surface-variant m-0">{k.hint}</p>
            </div>
          ))}
        </div>
      )}

      {/* ── Filters ── */}
      <div className="prod-filters">
        <Select
          label="Status"
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
        >
          <option value="ALL">Todos</option>
          <option value="DRAFT">Rascunho</option>
          <option value="PLANNED">Planejada</option>
          <option value="RELEASED">Liberada</option>
          <option value="IN_PROGRESS">Em Produção</option>
          <option value="COMPLETED">Concluída</option>
          <option value="CLOSED">Fechada</option>
          <option value="CANCELLED">Cancelada</option>
        </Select>
        <Input
          label="Buscar"
          placeholder="Nº OP ou nome do produto..."
          value={searchTerm}
          onChange={(e) => { setSearchTerm(e.target.value); setPage(0); }}
        />
      </div>

      {/* ── Loading ── */}
      {isLoading && (
        <Card padding="1rem">
          <LoadingSkeleton count={6} height="48px" />
        </Card>
      )}

      {/* ── Error ── */}
      {error && !isLoading && (
        <Card padding="2rem">
          <div className="state-error" role="alert">
            <p>Erro ao carregar ordens de produção</p>
            <p className="state-detail">{(error as Error).message}</p>
            <Button size="sm" variant="outline" onClick={() => refetch()}>Tentar novamente</Button>
          </div>
        </Card>
      )}

      {/* ── Empty ── */}
      {!isLoading && !error && orders.length === 0 && (
        <Card padding="2rem">
          <div className="state-empty">
            <p>{searchTerm || statusFilter !== 'ALL' ? 'Nenhuma ordem encontrada com esses filtros.' : 'Nenhuma ordem de produção encontrada. Clique em "+ Nova OP" para criar.'}</p>
          </div>
        </Card>
      )}

      {/* ── List ── */}
      {!isLoading && !error && orders.length > 0 && (
        <Card padding="0">
          <CardHeader
            className="px-md pt-md"
            action={
              <span className="font-label-md text-label-md text-on-surface-variant tabular-nums">
                {total} {total === 1 ? 'ordem' : 'ordens'}
              </span>
            }
          >
            <h3 className="font-headline-sm text-headline-sm text-on-surface m-0">Fila de produção ativa</h3>
          </CardHeader>
          <div className="prod-table-wrap">
            <table className="prod-table">
              <thead>
                <tr>
                  <th>Nº OP</th>
                  <th>Produto</th>
                  <th style={{ textAlign: 'right' }}>Qtd</th>
                  <th style={{ textAlign: 'right' }}>Produzido</th>
                  <th style={{ width: '150px' }}>Progresso</th>
                  <th>Status</th>
                  <th>Início</th>
                  <th>Fim</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((o: ProductionOrder) => (
                  <tr key={o.id}>
                    <td style={{ fontFamily: 'monospace', fontWeight: 600 }}>{o.orderNumber}</td>
                    <td>{o.productName}</td>
                    <td className="prod-num">{o.plannedQuantity}</td>
                    <td className="prod-num">{o.producedQuantity}</td>
                    <td>
                      {/* Progresso real: apontado sobre planejado. Sem estimativa
                          de tempo — o dominio nao registra duracao prevista por
                          etapa, so inicio e fim. */}
                      <div className="prod-progress">
                        <div className="prod-progress__track">
                          <div
                            className="prod-progress__fill"
                            style={{
                              width: `${o.plannedQuantity > 0 ? Math.min(100, Math.round((o.producedQuantity / o.plannedQuantity) * 100)) : 0}%`,
                            }}
                            role="progressbar"
                            aria-valuenow={
                              o.plannedQuantity > 0
                                ? Math.min(100, Math.round((o.producedQuantity / o.plannedQuantity) * 100))
                                : 0
                            }
                            aria-valuemin={0}
                            aria-valuemax={100}
                            aria-label={`Progresso da ordem ${o.orderNumber}`}
                          />
                        </div>
                        <span className="prod-progress__pct">
                          {o.plannedQuantity > 0
                            ? Math.min(100, Math.round((o.producedQuantity / o.plannedQuantity) * 100))
                            : 0}
                          %
                        </span>
                      </div>
                    </td>
                    <td><Badge variant={STATUS_VARIANT[o.status] ?? 'neutral'} size="sm">{STATUS_LABEL[o.status] ?? o.status}</Badge></td>
                    <td>{o.scheduledStart ? formatDate(o.scheduledStart) : '-'}</td>
                    <td>{o.scheduledEnd ? formatDate(o.scheduledEnd) : '-'}</td>
                    <td>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => { setDetailId(o.id); setDetailOpen(true); }}
                      >
                        Detalhes
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="prod-pagination">
              <Button size="sm" variant="secondary" disabled={page <= 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Anterior</Button>
              <span className="prod-page-info">
                {page * limit + 1}-{Math.min(total, page * limit + orders.length)} de {total}{' '}
                {total === 1 ? 'ordem' : 'ordens'}
              </span>
              <Button size="sm" variant="secondary" disabled={page >= totalPages - 1} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
            </div>
          )}
        </Card>
      )}

      {/* ── Create Modal ── */}
      <Modal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Nova Ordem de Produção"
        footer={
          <>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Button>
            <Button onClick={handleCreate} loading={createMutation.isPending}>Criar OP</Button>
          </>
        }
      >
        <div className="prod-form">
          <Input label="Produto (ID) *" value={form.productId} onChange={(e) => setForm((p) => ({ ...p, productId: e.target.value }))} placeholder="UUID do produto" />
          <Input label="Nome do Produto *" value={form.productName} onChange={(e) => setForm((p) => ({ ...p, productName: e.target.value }))} placeholder="Ex: Mesa MDF 1,5m" />
          <Input label="Nº da OP *" value={form.orderNumber} onChange={(e) => setForm((p) => ({ ...p, orderNumber: e.target.value }))} placeholder="Ex: OP-2026-0001" />
          <Input label="Quantidade *" type="number" min="1" value={form.plannedQuantity} onChange={(e) => setForm((p) => ({ ...p, plannedQuantity: e.target.value }))} placeholder="Ex: 100" />
          <div className="prod-form__row">
            <Input label="Início Planejado" type="date" value={form.scheduledStart} onChange={(e) => setForm((p) => ({ ...p, scheduledStart: e.target.value }))} />
            <Input label="Fim Planejado" type="date" value={form.scheduledEnd} onChange={(e) => setForm((p) => ({ ...p, scheduledEnd: e.target.value }))} />
          </div>
          <label className="prod-form__checkbox">
            <input type="checkbox" checked={form.useActiveBom} onChange={(e) => setForm((p) => ({ ...p, useActiveBom: e.target.checked }))} />
            Usar BOM ativa automaticamente
          </label>
          <Input label="Observações" value={form.notes} onChange={(e) => setForm((p) => ({ ...p, notes: e.target.value }))} placeholder="Observações opcionais" />
        </div>
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={`OP: ${progress?.status ? STATUS_LABEL[progress.status] ?? progress.status : 'Carregando...'}`}
        footer={
          <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
            {detailId && NEXT_ACTIONS[progress?.status ?? '']?.map((act) => (
              <Button
                key={act.key}
                size="sm"
                variant="primary"
                style={act.color ? { background: act.color, borderColor: act.color } : undefined}
                onClick={() => {
                  setActionModal({ id: detailId, action: act.key });
                  // Pre-fill forms
                  if (act.key === 'plan') {
                    const order = orders.find((o) => o.id === detailId);
                    setPlanForm({
                      bomId: activeBoms[0]?.id ?? '',
                      scheduledStart: order?.scheduledStart?.split('T')[0] ?? '',
                      scheduledEnd: order?.scheduledEnd?.split('T')[0] ?? '',
                    });
                  }
                  if (act.key === 'release') setReleaseBomId(activeBoms[0]?.id ?? '');
                  if (act.key === 'cancel') setCancelJustification('');
                }}
              >
                {act.label}
              </Button>
            ))}
          </div>
        }
      >
        {detailId && (
          <div className="prod-detail">
            {/* Summary */}
            <div className="prod-detail__stat-grid">
              <div className="prod-detail__stat">
                <span className="prod-stat-label">Status</span>
                <Badge variant={STATUS_VARIANT[progress?.status ?? 'DRAFT'] ?? 'neutral'} size="sm">
                  {STATUS_LABEL[progress?.status ?? 'DRAFT']}
                </Badge>
              </div>
              <div className="prod-detail__stat">
                <span className="prod-stat-label">Progresso</span>
                <span className="prod-stat-value">
                  {progress ? `${progress.yield_.toFixed(0)}%` : '-'}
                </span>
              </div>
              <div className="prod-detail__stat">
                <span className="prod-stat-label">Produzido</span>
                <span className="prod-stat-value">{progress?.producedQuantity ?? 0} / {progress?.plannedQuantity ?? 0}</span>
              </div>
              {progress && hasMaterialShortage(progress) && (
                <div className="prod-detail__stat prod-detail__stat--warn">
                  <span className="prod-stat-label">Falta material</span>
                  <span className="prod-stat-value">
                    ⚠ {progress.materialConsumptionPercent.toFixed(0)}% consumido
                  </span>
                </div>
              )}
              {progress?.isOverdue && (
                <div className="prod-detail__stat prod-detail__stat--warn">
                  <span className="prod-stat-label">Prazo</span>
                  <span className="prod-stat-value">⚠ Atrasada</span>
                </div>
              )}
            </div>

            {/* Costs */}
            {costs && (
              <div className="prod-detail__costs">
                <h4>Custos</h4>
                <div className="prod-cost-grid">
                  <div><span className="prod-stat-label">Material</span><span className="prod-stat-value">{formatCurrency(costs.materialCost)}</span></div>
                  <div><span className="prod-stat-label">Esperado</span><span className="prod-stat-value">{formatCurrency(costs.expectedCost)}</span></div>
                  <div><span className="prod-stat-label">Variação</span><span className={`prod-stat-value ${costs.variance > 0 ? 'prod-pos' : 'prod-neg'}`}>{formatCurrency(costs.variance)} ({costs.variancePercent.toFixed(1)}%)</span></div>
                  <div><span className="prod-stat-label">Custo/Un</span><span className="prod-stat-value">{formatCurrency(costs.costPerUnit)}</span></div>
                </div>
              </div>
            )}

            {/* Consumed Materials */}
            {progress && (
              <div className="prod-detail__materials">
                <h4>Materiais</h4>
                <div className="prod-mat-table-wrap">
                  <table className="prod-mat-table">
                    <thead>
                      <tr><th>Produto</th><th>Previsto</th><th>Consumido</th><th>Unid.</th></tr>
                    </thead>
                    <tbody>
                      {progress.materialRequirements.map((m) => (
                        <tr key={m.productId}>
                          <td>{m.productName ?? m.productId}</td>
                          <td>{m.requiredQuantity}</td>
                          <td className={m.consumedQuantity < m.requiredQuantity ? 'prod-neg' : 'prod-pos'}>
                            {m.consumedQuantity}
                          </td>
                          <td>{m.unit}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Postings */}
            {postings && postings.length > 0 && (
              <div className="prod-detail__postings">
                <h4>Movimentações</h4>
                <div className="prod-mat-table-wrap">
                  <table className="prod-mat-table">
                    <thead>
                      <tr><th>Tipo</th><th>Produto</th><th>Qtd</th><th>Custo Un</th><th>Total</th><th>Data</th></tr>
                    </thead>
                    <tbody>
                      {postings.map((p: any) => (
                        <tr key={p.id}>
                          <td><Badge variant="info" size="sm">{p.type}</Badge></td>
                          <td style={{ fontFamily: 'monospace', fontSize: '0.8125rem' }}>{p.productId.slice(0, 8)}</td>
                          <td>{p.quantity}</td>
                          <td>{formatCurrency(p.unitCost)}</td>
                          <td>{formatCurrency(p.totalCost)}</td>
                          <td>{formatDate(p.createdAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* ── Action Modals ── */}
      <Modal
        isOpen={actionModal?.action === 'plan'}
        onClose={() => setActionModal(null)}
        title="Planejar Ordem"
        footer={<><Button variant="ghost" onClick={() => setActionModal(null)}>Cancelar</Button><Button onClick={handleAction} loading={planMutation.isPending}>Planejar</Button></>}
      >
        <div className="prod-form">
          <Select label="Estrutura (BOM) *" value={planForm.bomId} onChange={(e) => setPlanForm((p) => ({ ...p, bomId: e.target.value }))}>
            <option value="">Selecione...</option>
            {activeBoms.map((b) => <option key={b.id} value={b.id}>{b.description || b.productId}</option>)}
          </Select>
          <Input label="Início *" type="date" value={planForm.scheduledStart} onChange={(e) => setPlanForm((p) => ({ ...p, scheduledStart: e.target.value }))} />
          <Input label="Fim *" type="date" value={planForm.scheduledEnd} onChange={(e) => setPlanForm((p) => ({ ...p, scheduledEnd: e.target.value }))} />
        </div>
      </Modal>

      <Modal isOpen={actionModal?.action === 'release'} onClose={() => setActionModal(null)} title="Liberar Ordem"
        footer={<><Button variant="ghost" onClick={() => setActionModal(null)}>Cancelar</Button><Button onClick={handleAction} loading={releaseMutation.isPending}>Liberar</Button></>}>
        <div className="prod-form">
          <Select label="Estrutura (BOM) *" value={releaseBomId} onChange={(e) => setReleaseBomId(e.target.value)}>
            <option value="">Selecione...</option>
            {activeBoms.map((b) => <option key={b.id} value={b.id}>{b.description || b.productId}</option>)}
          </Select>
        </div>
      </Modal>

      <Modal isOpen={actionModal?.action === 'consume'} onClose={() => setActionModal(null)} title="Consumir Material"
        footer={<><Button variant="ghost" onClick={() => setActionModal(null)}>Cancelar</Button><Button onClick={handleAction} loading={consumeMutation.isPending}>Consumir</Button></>}>
        <div className="prod-form">
          <Input label="Produto (ID) *" value={consumeForm.productId} onChange={(e) => setConsumeForm((p) => ({ ...p, productId: e.target.value }))} placeholder="UUID" />
          <div className="prod-form__row">
            <Input label="Quantidade *" type="number" min="0.01" step="0.01" value={consumeForm.quantity} onChange={(e) => setConsumeForm((p) => ({ ...p, quantity: e.target.value }))} />
            <Input label="Custo Unitário *" type="number" min="0" step="0.01" value={consumeForm.unitCost} onChange={(e) => setConsumeForm((p) => ({ ...p, unitCost: e.target.value }))} />
          </div>
          <Input label="Lote (opcional)" value={consumeForm.lotNumber} onChange={(e) => setConsumeForm((p) => ({ ...p, lotNumber: e.target.value }))} />
          <Input label="Observações" value={consumeForm.notes} onChange={(e) => setConsumeForm((p) => ({ ...p, notes: e.target.value }))} />
        </div>
      </Modal>

      <Modal isOpen={actionModal?.action === 'post-output'} onClose={() => setActionModal(null)} title="Apontar Produção"
        footer={<><Button variant="ghost" onClick={() => setActionModal(null)}>Cancelar</Button><Button onClick={handleAction} loading={postMutation.isPending}>Apontar</Button></>}>
        <div className="prod-form">
          <Input label="Quantidade Produzida *" type="number" min="0" step="1" value={postForm.producedQuantity} onChange={(e) => setPostForm((p) => ({ ...p, producedQuantity: e.target.value }))} />
          <Input label="Refugo" type="number" min="0" step="1" value={postForm.wasteQuantity} onChange={(e) => setPostForm((p) => ({ ...p, wasteQuantity: e.target.value }))} />
          <Input label="Custo Unitário *" type="number" min="0" step="0.01" value={postForm.unitCost} onChange={(e) => setPostForm((p) => ({ ...p, unitCost: e.target.value }))} />
          <Input label="Lote (opcional)" value={postForm.lotNumber} onChange={(e) => setPostForm((p) => ({ ...p, lotNumber: e.target.value }))} />
          <Input label="Observações" value={postForm.notes} onChange={(e) => setPostForm((p) => ({ ...p, notes: e.target.value }))} />
        </div>
      </Modal>

      <Modal isOpen={actionModal?.action === 'cancel'} onClose={() => setActionModal(null)} title="Cancelar Ordem"
        footer={<><Button variant="ghost" style={{ color: '#ba1a1a' }} onClick={() => setActionModal(null)}>Voltar</Button><Button style={{ background: '#ba1a1a', borderColor: '#ba1a1a' }} onClick={handleAction} loading={cancelMutation.isPending}>Confirmar Cancelamento</Button></>}>
        <div className="prod-form">
          <p style={{ fontSize: '0.875rem', color: '#ba1a1a', margin: '0 0 0.75rem' }}>Tem certeza? Esta ação não pode ser desfeita.</p>
          <Input label="Justificativa *" value={cancelJustification} onChange={(e) => setCancelJustification(e.target.value)} placeholder="Motivo do cancelamento" />
        </div>
      </Modal>

      {/* ── Close confirm modal ── */}
      <Modal isOpen={actionModal?.action === 'close'} onClose={() => setActionModal(null)} title="Fechar Ordem"
        footer={<><Button variant="ghost" onClick={() => setActionModal(null)}>Cancelar</Button><Button onClick={handleAction} loading={closeMutation.isPending}>Fechar</Button></>}>
        <p style={{ fontSize: '0.875rem', color: '#43474e', margin: 0 }}>
          Tem certeza que deseja fechar esta ordem? O custo final será calculado e a ordem será encerrada.
        </p>
      </Modal>

      <style>{`
        .prod-kpi {
          background: #ffffff;
          border: 1px solid #c4c6cf;
          border-radius: 8px;
          padding: 1rem;
          box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
        }
        .prod-kpi__top {
          display: flex;
          align-items: center;
          gap: 0.375rem;
          margin-bottom: 0.25rem;
        }
        .prod-kpi__top .material-symbols-outlined { font-size: 18px; color: #43474e; }

        .prod-num {
          text-align: right;
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-variant-numeric: tabular-nums;
        }
        .prod-progress { display: flex; align-items: center; gap: 0.5rem; }
        .prod-progress__track {
          flex: 1 1 auto;
          height: 6px;
          border-radius: 3px;
          background: #e0e3e5;
          overflow: hidden;
        }
        .prod-progress__fill { height: 100%; background: #002045; }
        .prod-progress__pct {
          font-family: 'JetBrains Mono', ui-monospace, monospace;
          font-size: 12px;
          color: #43474e;
          min-width: 34px;
          text-align: right;
        }

        .prod-page { padding: 1.5rem; }
        .prod-filters { display: flex; gap: 0.75rem; margin-bottom: 1rem; flex-wrap: wrap; }
        .prod-filters > * { min-width: 180px; flex: 1; max-width: 300px; }
        .prod-table-wrap { overflow-x: auto; }
        .prod-table { width: 100%; border-collapse: collapse; font-size: 0.875rem; }
        .prod-table th { padding: 0.75rem 1rem; background: #f2f4f6; color: #43474e; font-weight: 600; font-size: 0.75rem; text-transform: uppercase; letter-spacing: 0.05em; border-bottom: 2px solid #e0e3e5; text-align: left; white-space: nowrap; position: sticky; top: 0; z-index: 1;}
        .prod-table td { padding: 0.75rem 1rem; border-bottom: 1px solid #eceef0; color: #191c1e; }
        .prod-table tbody tr:hover td { background: #eceef0; }
        .prod-pagination { display: flex; align-items: center; justify-content: center; gap: 1rem; padding: 1rem; }
        .prod-page-info { font-size: 0.8125rem; color: #43474e; }
        .prod-form { display: flex; flex-direction: column; gap: 1rem; min-width: 380px; }
        .prod-form__row { display: flex; gap: 0.75rem; }
        .prod-form__row > * { flex: 1; }
        .prod-form__checkbox { display: flex; align-items: center; gap: 0.5rem; font-size: 0.875rem; color: #43474e; cursor: pointer; }
        .state-error { text-align: center; }
        .state-error p { margin: 0; color: #ba1a1a; font-weight: 600; }
        .state-error .state-detail { color: #74777f; font-size: 0.875rem; font-weight: 400; margin-top: 0.5rem; }
        .state-empty { text-align: center; padding: 1rem; color: #74777f; }
        .prod-detail__stat-grid { display: flex; gap: 0.75rem; flex-wrap: wrap; margin-bottom: 1rem; }
        .prod-detail__stat { flex: 1; min-width: 100px; padding: 0.75rem; background: #f2f4f6; border-radius: 4px; text-align: center; }
        .prod-detail__stat--warn { background: #ffddba; }
        .prod-stat-label { display: block; font-size: 12px; color: #43474e; margin-bottom: 0.25rem; font-family: 'JetBrains Mono', ui-monospace, monospace; font-weight: 500; letter-spacing: 0.02em;}
        .prod-stat-value { font-size: 0.9375rem; font-weight: 600; color: #191c1e; }
        .prod-detail__costs h4, .prod-detail__materials h4, .prod-detail__postings h4 { margin: 1rem 0 0.5rem; font-size: 0.9375rem; }
        .prod-cost-grid { display: flex; gap: 0.75rem; flex-wrap: wrap; }
        .prod-cost-grid > div { flex: 1; min-width: 120px; padding: 0.5rem; background: #f2f4f6; border-radius: 4px; text-align: center; }
        .prod-pos { color: #2e7d32; }
        .prod-neg { color: #ba1a1a; }
        .prod-mat-table-wrap { overflow-x: auto; }
        .prod-mat-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
        .prod-mat-table th { text-align: left; padding: 0.5rem 0.75rem; background: #f2f4f6; color: #43474e; font-size: 0.75rem; border-bottom: 2px solid #e0e3e5; position: sticky; top: 0; z-index: 1;}
        .prod-mat-table td { padding: 0.375rem 0.75rem; border-bottom: 1px solid #eceef0; }
      `}</style>
    </div>
  );
}
